import React, {useEffect, useMemo, useRef, useState} from 'react';
import {classes, style, vars} from './doppe-layout11-profile-view-template.st.css';
import {BymoTemplateMetadataInferBymoPageProps} from '../../../doppe-sdk';
import {useElementClientAreaBinding} from '@wix/devzai-utils-react';
import {arrayFirst} from '@wix/devzai-utils-common';
import {FlexLayout} from '@wix/devzai-common-client';
import {doppeHideableValueIsVisible} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {
    DoppePageProfileNameView,
    DoppePageProfileNameViewVerifiedCheckAppearance
} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {DoppeProfileNameAutoSource} from '../../../client-server-common';
import {
    BymoTemplateSpeedDialTemplateMetadata
} from '../../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial.metadata';
import {DoppeLayoutRoot} from '../dopp-layouts-components/doppe-layout-root/doppe-layout-root';
import {DoppLayoutContent} from '../dopp-layouts-components/dopp-layout-content/dopp-layout-content';
import {DoppLayoutBackground} from '../dopp-layouts-components/dopp-layout-background/dopp-layout-background';
import {
    DoppLayoutBackgroundMedia
} from '../dopp-layouts-components/dopp-layout-background-media/dopp-layout-background-media';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {DoppeBackgroundView} from '../../doppe-background-view/doppe-background-view';
import {DoppLayoutJumpingArrow} from '../dopp-layouts-components/dopp-layout-jumping-arrow/dopp-layout-jumping-arrow';
import {useDoppeViewerClientServices} from '../../../client/doppe-user-app/doppe-viewer-client-services';
import {DoppeLayoutPageContent} from '../dopp-layouts-components/doppe-layout-page-content/doppe-layout-page-content';
import {DoppeLayoutBioText} from '../dopp-layouts-components/doppe-layout-bio-text/doppe-layout-bio-text';
import {DoppeLayoutSocialLinks} from '../dopp-layouts-components/doppe-layout-social-links/doppe-layout-social-links';

export const DoppeLayout11ProfileViewTemplate = React.memo(function DoppeLayout8ProfileViewTemplate(props: BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata>) {
    const {
        profilePictureLinkInBio,
        bioText,
        bioTextMaxLines,
        socialLinks,
        heroPhotos,
    } = props;

    const hasProfileImage = useMemo(() => {
        return doppeHideableValueIsVisible(profilePictureLinkInBio);
    }, [profilePictureLinkInBio]);

    const [opacity, setOpacity] = useState(0);

    const contentRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const clientArea = useElementClientAreaBinding(contentRef) ?? 0;
    const imageArea = useElementClientAreaBinding(imageRef) ?? 0;

    const doppeViewerClientServices = useDoppeViewerClientServices();

    useEffect(() => {
        if (clientArea === 0 || imageArea === 0) {
            setOpacity(0)
        } else {
            const progress = cubicBezierEasing(
                Math.min(1, Math.max(0, (-clientArea.top) / (imageArea.height))),
                0, 0.85, 0.32, 0.98
            );
            setOpacity(progress);
        }
    }, [clientArea, imageArea]);

    return (
        <DoppeLayoutRoot
            className={style(classes.root, {})}
            style={{
                [vars.opacity]: 1 - opacity
            }}
            hideHoppBanner={props.removeHoppBanner}
        >
            <DoppLayoutBackground
                className={style(classes.coverBackground)}
            >
                <DoppLayoutBackgroundMedia
                    className={style(classes.pageWithCoverBackgroundView, {})}
                />
            </DoppLayoutBackground>
            <div
                className={style(classes.firstContentWrapper, {}, FlexLayout.column.default)}
                ref={contentRef}
            >
                <DoppLayoutContent
                    className={style(classes.firstContent, FlexLayout.column.default)}
                >
                    <div
                        className={style(classes.profileView, FlexLayout.column.default)}
                    >
                        <div
                            className={style(classes.profileViewContent, {}, FlexLayout.column.alignToCenter)}
                        >
                            {
                                hasProfileImage ?
                                    <DoppeHideableValueRenderer
                                        hideableValue={profilePictureLinkInBio}
                                        renderFunction={profilePicture => (
                                            <DoppePageProfilePicture
                                                className={style(classes.profileImage, {})}
                                                profilePicture={profilePicture}
                                                size={74}
                                            />
                                        )}
                                    />
                                    :
                                    null
                            }
                            <DoppePageProfileNameView
                                className={style(classes.profileName, {}, FlexLayout.verticallyJustifyToEnd)}
                                defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                                verifiedCheckAppearance={DoppePageProfileNameViewVerifiedCheckAppearance.Superscript}
                            />
                            <DoppeLayoutBioText
                                className={style(classes.bioText, {})}
                                bioText={bioText}
                                bioTextMaxLines={bioTextMaxLines}
                                lineHeight={'18px'}
                            />
                            <DoppeLayoutSocialLinks
                                className={style(classes.socialLinks, {}) }
                                socialLinks={socialLinks}
                            />
                            <DoppLayoutJumpingArrow
                                className={style(
                                    classes.downButton, {}
                                )}
                                onClick={async () => {
                                    await doppeViewerClientServices.uiService.pageActionsList?.scrollToActionsListContainer();
                                }}
                            />

                        </div>
                    </div>
                </DoppLayoutContent>
                <div
                    className={style(classes.coverImageContainer, {})}
                    ref={imageRef}
                >
                    <DoppeBackgroundView
                        className={style(classes.heroPhoto)}
                        heroPhoto={arrayFirst(heroPhotos ?? [])}
                    />
                    <div className={style(classes.heroPhotoOverlay, {})}/>
                </div>
            </div>
            <DoppLayoutContent
                className={style(classes.content)}
            >
                <DoppeLayoutPageContent
                    className={style(classes.pageContent, {})}
                    pageProps={props}
                />
            </DoppLayoutContent>
        </DoppeLayoutRoot>
    )
});


// Easing function (Quadratic ease-in-out)
function cubicBezierEasing(t: number, p0: number, p1: number, p2: number, p3: number) {
    return (
        (1 - t) ** 3 * p0 +
        3 * (1 - t) ** 2 * t * p1 +
        3 * (1 - t) * t ** 2 * p2 +
        t ** 3 * p3
    );
}
